import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Asistente APS — panel de chat con IA: streaming, markdown (tablas/negritas/listas),
// efecto "escribiendo…", cards de entidades, acciones con confirmación y feedback.
// Reutilizable: drawer global, página dedicada y EMBEBIDO dentro del Chat existente.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, IconButton, Avatar, Chip, Stack, Paper,
  CircularProgress, Tooltip, Button, Menu, MenuItem, ListItemText, Divider,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { api } from '../../services/api';
import AIProposalCards from './AIEntityCards';
import Markdown from './Markdown';

const SUGGESTIONS = [
  i18n.t('hardcoded.components_ai_assistantpanel.que_debo_revisar_primero_hoy'),
  i18n.t('hardcoded.components_ai_assistantpanel.muestrame_los_tickets_criticos_abiertos'),
  i18n.t('hardcoded.components_ai_assistantpanel.resume_lo_mas_importante_de'),
  i18n.t('hardcoded.components_ai_assistantpanel.que_tareas_tengo_pendientes'),
];

const RED = '#d32f2f';

export default function AssistantPanel({ initialPrompt = '', channelId = null, embedded = false, pageContext = null }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [toolStatus, setToolStatus] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [feedbackById, setFeedbackById] = useState({});
  const [histAnchor, setHistAnchor] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const sentInitial = useRef(false);
  const atBottomRef = useRef(true);
  const lastContentRef = useRef({ value: '' }).current;

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    if (!force && !atBottomRef.current) return; // no arrebatar el scroll si el usuario subió
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, toolStatus, streaming, scrollToBottom]);

  const send = useCallback(async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput('');
    setBusy(true);
    setToolStatus(t('hardcoded.components_ai_assistantpanel.analizando_el_sistema'));
    setStreaming(false);
    atBottomRef.current = true; // al enviar, seguimos la respuesta
    lastContentRef.value = '';
    setMessages((prev) => [...prev, { role: 'user', content }, { role: t('hardcoded.components_ai_assistantpanel.assistant'), content: '', streaming: true }]);

    const controller = new AbortController();
    abortRef.current = controller;

    const updateAssistant = (patch) => {
      setMessages((prev) => {
        const next = [...prev];
        const i = next.length - 1;
        if (i >= 0 && next[i].role === 'assistant') next[i] = { ...next[i], ...patch };
        return next;
      });
    };

    try {
      await api.ai.streamChat(
        { message: content, conversation_id: conversationId, channel_id: channelId, page_context: pageContext },
        {
          signal: controller.signal,
          onEvent: (evt) => {
            switch (evt.type) {
              case 'ai_message_started':
                if (evt.conversation_id) setConversationId(evt.conversation_id);
                break;
              case 'ai_tool_call_started':
                setToolStatus(t('hardcoded.components_ai_assistantpanel.consultando_labelfortoolevt', { labelForToolevt: labelForTool(evt.tool) }));
                break;
              case 'ai_tool_call_completed':
                setToolStatus(t('hardcoded.components_ai_assistantpanel.redactando_respuesta'));
                break;
              case 'ai_message_delta':
                setStreaming(true);
                setToolStatus('');
                updateAssistant({ content: (lastContentRef.value += evt.delta) });
                break;
              case 'ai_message_reset':
                // El turno mostró texto parcial pero luego decidió usar herramientas:
                // descartamos ese parcial (no es la respuesta final).
                lastContentRef.value = '';
                updateAssistant({ content: '' });
                break;
              case 'ai_message_completed':
                updateAssistant({
                  content: evt.content || lastContentRef.value,
                  structured_payload: evt.structured_payload,
                  message_id: evt.structured_payload?.message_id,
                  streaming: false,
                });
                lastContentRef.value = '';
                break;
              case 'ai_error':
                updateAssistant({ content: evt.message, error: true, streaming: false });
                break;
              default:
                break;
            }
          },
        },
      );
    } catch (e) {
      updateAssistant({ content: t('hardcoded.components_ai_assistantpanel.no_se_pudo_conectar_con'), error: true, streaming: false });
    } finally {
      setBusy(false);
      setToolStatus('');
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, busy, conversationId, channelId, pageContext, lastContentRef]);

  useEffect(() => {
    if (initialPrompt && !sentInitial.current) {
      sentInitial.current = true;
      send(initialPrompt);
    }
  }, [initialPrompt, send]);

  const sendFeedback = async (msg, rating) => {
    if (!msg.message_id) return;
    setFeedbackById((p) => ({ ...p, [msg.message_id]: rating }));
    try { await api.ai.feedback({ ai_message_id: msg.message_id, rating }); } catch (e) { /* noop */ }
  };

  const openHistory = async (e) => {
    setHistAnchor(e.currentTarget);
    setLoadingHist(true);
    try { const r = await api.ai.listConversations(40); setConversations(r.data || []); }
    catch (err) { setConversations([]); }
    finally { setLoadingHist(false); }
  };

  const openConversation = async (id) => {
    setHistAnchor(null);
    if (id === conversationId) return;
    setLoadingConv(true);
    try {
      const r = await api.ai.getConversation(id);
      const msgs = (r.data?.messages || [])
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          role: m.role,
          content: m.content || '',
          structured_payload: m.structured_payload || {},
          message_id: m.role === 'assistant' ? m.id : undefined,
          error: m.status === 'error',
        }));
      setMessages(msgs);
      setConversationId(id);
      setFeedbackById({});
    } catch (err) { /* noop */ }
    finally { setLoadingConv(false); }
  };

  const newConversation = () => {
    if (busy) return;
    setMessages([]);
    setConversationId(null);
    setFeedbackById({});
    lastContentRef.value = '';
  };

  const showTyping = busy && (!!toolStatus || (messages.length > 0 && messages[messages.length - 1].role === 'assistant' && !messages[messages.length - 1].content));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: embedded ? 'grey.50' : 'background.default' }}>
      {/* Toolbar: historial + nueva conversación */}
      <Box sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
        borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Button size="small" startIcon={<HistoryIcon sx={{ fontSize: 18 }} />} onClick={openHistory}
          sx={{ color: 'text.secondary', textTransform: 'none' }}>
          {i18n.t('hardcoded.components_ai_assistantpanel.historial')}</Button>
        <Box sx={{ flex: 1 }} />
        {loadingConv && <CircularProgress size={14} sx={{ mr: 1 }} />}
        <Tooltip title={t('hardcoded.components_ai_assistantpanel.nueva_conversacion')}>
          <span>
            <Button size="small" startIcon={<AddCommentIcon sx={{ fontSize: 18 }} />} onClick={newConversation}
              disabled={busy || messages.length === 0}
              sx={{ color: 'primary.main', textTransform: 'none' }}>
              {i18n.t('hardcoded.components_ai_assistantpanel.nueva')}</Button>
          </span>
        </Tooltip>
      </Box>

      <Menu anchorEl={histAnchor} open={Boolean(histAnchor)} onClose={() => setHistAnchor(null)}
        PaperProps={{ sx: { maxHeight: 380, width: 320 } }}>
        <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
          CONVERSACIONES
        </Typography>
        <Divider />
        {loadingHist && (
          <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={18} /></Box>
        )}
        {!loadingHist && conversations.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            {t('hardcoded.components_ai_assistantpanel.aun_no_tienes_conversaciones_previas')}</Typography>
        )}
        {!loadingHist && conversations.map((c) => (
          <MenuItem key={c.id} selected={c.id === conversationId} onClick={() => openConversation(c.id)}>
            <ListItemText
              primary={<Typography variant="body2" noWrap fontWeight={c.id === conversationId ? 700 : 500}>{c.title || t('hardcoded.components_ai_assistantpanel.conversacion')}</Typography>}
              secondary={c.updated_at ? new Date(c.updated_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
            />
          </MenuItem>
        ))}
      </Menu>

      <Box ref={scrollRef} onScroll={onScroll} sx={{ flex: 1, overflowY: 'auto', p: { xs: 1.5, sm: 2.5 }, minHeight: 0 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: { xs: 3, sm: 6 }, color: 'text.secondary' }}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
              <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 1.5, background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)', boxShadow: '0 6px 20px rgba(211,47,47,0.35)' }}>
                <AutoAwesomeIcon sx={{ fontSize: 34 }} />
              </Avatar>
            </motion.div>
            <Typography variant="h6" fontWeight={800} color="text.primary">{i18n.t('hardcoded.components_ai_assistantlauncher.asistente_aps')}</Typography>
            <Typography variant="body2" sx={{ mb: 2.5, maxWidth: 380, mx: 'auto' }}>
              {t('hardcoded.components_ai_assistantpanel.tu_copiloto_operativo_preguntame_por')}</Typography>
            <Stack spacing={1} sx={{ maxWidth: 380, mx: 'auto' }}>
              {SUGGESTIONS.map((s, i) => (
                <motion.div key={s} initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <Chip label={s} variant="outlined" onClick={() => send(s)}
                    icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    sx={{ width: '100%', justifyContent: 'flex-start', py: 2, cursor: 'pointer',
                      '&:hover': { borderColor: RED, bgcolor: 'rgba(211,47,47,0.04)' } }} />
                </motion.div>
              ))}
            </Stack>
          </Box>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              <MessageRow
                msg={m}
                feedback={feedbackById[m.message_id]}
                onFeedback={(rating) => sendFeedback(m, rating)}
                onConfirm={() => send(t('hardcoded.components_ai_assistantpanel.si_confirmo_procede_con_la'))}
                onCancel={() => send(t('hardcoded.components_ai_assistantpanel.no_cancela_esa_accion'))}
                onSuggestion={(txt) => send(txt)}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {showTyping && <TypingBubble status={toolStatus} />}
      </Box>

      {/* Composer */}
      {/* El composer nunca se encoge: con el teclado abierto en movil el flex
          lo comprimia y el campo de texto quedaba inutilizable. */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
        display: 'flex', gap: 1, alignItems: 'flex-end', flexShrink: 0 }}>
        <TextField
          fullWidth multiline maxRows={5} size="small" placeholder={t('hardcoded.components_ai_assistantpanel.escribe_tu_pregunta_enter_para')}
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={busy}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <IconButton onClick={() => send()} disabled={busy || !input.trim()}
          sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)', color: '#fff', width: 42, height: 42,
            '&:hover': { background: 'linear-gradient(135deg, #b71c1c 0%, #8e1313 100%)' }, '&.Mui-disabled': { background: '#e0e0e0' } }}>
          {busy ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}

function TypingBubble({ status }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end' }}>
      <Avatar sx={{ width: 30, height: 30, background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)' }}>
        <AutoAwesomeIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Paper variant="outlined" sx={{ px: 1.5, py: 1.25, borderRadius: '14px 14px 14px 4px', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Stack direction="row" spacing={0.5}>
          {[0, 1, 2].map((i) => (
            <motion.span key={i}
              style={{ width: 7, height: 7, borderRadius: '50%', background: RED, display: 'inline-block' }}
              animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }} />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">{status || t('hardcoded.components_ai_assistantpanel.asistente_aps_esta_escribiendo')}</Typography>
      </Paper>
    </Box>
  );
}

function MessageRow({ msg, feedback, onFeedback, onConfirm, onCancel, onSuggestion }) {
  const { t } = useTranslation();
  const isUser = msg.role === 'user';
  const payload = msg.structured_payload || {};
  const sources = payload.sources || [];
  const proposals = payload.proposals || [];
  const suggestions = payload.suggestions || [];

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Paper elevation={0} sx={{ px: 1.75, py: 1, maxWidth: '85%',
          background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)', color: '#fff',
          borderRadius: '14px 14px 4px 14px' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: '#fff' }}>{msg.content}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
      <Avatar sx={{ width: 30, height: 30, mt: 0.25, background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)' }}>
        <AutoAwesomeIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ maxWidth: '90%', minWidth: 0 }}>
        <Paper elevation={0} variant="outlined" sx={{ px: 1.75, py: 1, borderRadius: '14px 14px 14px 4px',
          borderColor: msg.error ? 'error.main' : 'divider', bgcolor: 'background.paper' }}>
          {msg.error ? (
            <Typography variant="body2" color="error.main">{msg.content}</Typography>
          ) : msg.streaming ? (
            // Mientras llega el stream mostramos texto plano legible (sin reparsear
            // Markdown en cada token) + cursor; al terminar renderizamos Markdown.
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.primary' }}>
              {msg.content}
              <Box component="span" sx={{ ml: 0.25, animation: 'apsBlink 1s step-end infinite', '@keyframes apsBlink': { '50%': { opacity: 0 } } }}>▋</Box>
            </Typography>
          ) : (
            <Markdown>{msg.content}</Markdown>
          )}
        </Paper>

        {proposals.length > 0 && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {proposals.map((p, i) => (
              <Paper key={i} sx={{ p: 1.25, bgcolor: 'rgba(255,152,0,0.06)', border: '1px dashed', borderColor: 'warning.main', borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                  <Typography variant="body2" fontWeight={600}>{p.message || t('hardcoded.components_ai_assistantpanel.accion_propuesta')}</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" color="primary" onClick={onConfirm}>{t('common.confirm')}</Button>
                  <Button size="small" variant="outlined" onClick={onCancel}>{t('common.cancel')}</Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        <AIProposalCards payload={payload} />

        {!msg.streaming && suggestions.length > 0 && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
            {suggestions.map((s, i) => (
              <Chip key={i} label={s} size="small" variant="outlined" clickable
                icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                onClick={() => onSuggestion(s)}
                sx={{ borderColor: RED, color: '#8e1313', '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' } }} />
            ))}
          </Stack>
        )}

        {!msg.streaming && !msg.error && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            {sources.map((s) => (
              <Chip key={s} size="small" variant="outlined" label={labelForTool(s)}
                sx={{ height: 20, fontSize: 11, color: 'text.secondary' }} />
            ))}
            <Box sx={{ flex: 1 }} />
            {msg.message_id && (
              <>
                <Tooltip title={t('hardcoded.components_ai_assistantpanel.util')}>
                  <IconButton size="small" onClick={() => onFeedback('up')}>
                    {feedback === 'up' ? <ThumbUpAltIcon fontSize="inherit" color="primary" /> : <ThumbUpAltOutlinedIcon fontSize="inherit" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('hardcoded.components_ai_assistantpanel.no_util')}>
                  <IconButton size="small" onClick={() => onFeedback('down')}>
                    {feedback === 'down' ? <ThumbDownAltIcon fontSize="inherit" color="error" /> : <ThumbDownAltOutlinedIcon fontSize="inherit" />}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

const TOOL_LABELS = {
  get_open_tickets: i18n.t('hardcoded.components_ai_aientitycards.tickets_abiertos'), get_ticket_detail: i18n.t('hardcoded.components_ai_assistantpanel.detalle_de_ticket'),
  get_vehicle_summary: i18n.t('vehicles.table.vehicle'), get_vehicle_live_status: i18n.t('hardcoded.components_ai_assistantpanel.estado_del_vehiculo'),
  get_vehicle_tickets: i18n.t('vehicleTicketsDrawer.title'), get_vehicle_telemetry_summary: i18n.t('hardcoded.components_ai_assistantpanel.lecturas'),
  get_tasks_summary: i18n.t('hardcoded.components_ai_assistantpanel.tareas'), get_task_detail: i18n.t('hardcoded.components_ai_assistantpanel.detalle_de_tarea'), get_company_summary: i18n.t('navbar.company'),
  get_action_summary: i18n.t('hardcoded.components_ai_assistantpanel.plan_de_accion'), get_tpms_report_summary: i18n.t('hardcoded.components_ai_assistantpanel.reporte_tpms'),
  get_sensors_status: i18n.t('sensors.title'), get_receivers_status: i18n.t('receivers.title'),
  get_recent_chat_messages: i18n.t('hardcoded.components_ai_assistantpanel.mensajes_del_canal'), search_system_entities: i18n.t('hardcoded.components_ai_assistantpanel.busqueda'),
  get_alarm_profile_detail: i18n.t('hardcoded.components_ai_assistantpanel.perfil_de_alarma'), create_task: i18n.t('hardcoded.components_tickets_ticketactioncenter.crear_tarea'), save_ai_memory: i18n.t('hardcoded.components_ai_assistantpanel.memoria'),
  get_ai_memory: i18n.t('hardcoded.components_ai_assistantpanel.memoria'), get_chat_channel_context: i18n.t('hardcoded.components_ai_assistantpanel.canal'), get_user_preferences: i18n.t('hardcoded.components_ai_assistantpanel.preferencias'),
  list_ai_recommendations: i18n.t('hardcoded.components_ai_assistantpanel.recomendaciones'),
};
function labelForTool(t) { return TOOL_LABELS[t] || (t || '').replace(/_/g, ' '); }
